import React, { useEffect, useMemo, useState } from "react";
import {
  Search, Mail, KeyRound, Plus, Minus, FolderLock, Clock,
  ShoppingBag, X, CheckCircle2, AlertCircle, RefreshCw, Filter,
} from "lucide-react";
import api from "../lib/api";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
}

export default function UsersAdmin({ allCases }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | paid | no_purchase
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [showOrdersFor, setShowOrdersFor] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchUsers = async (query = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ filter });
      if (query) params.set("q", query);
      const r = await api.get(`/admin/users?${params.toString()}`);
      setUsers(r.data.users || []);
    } catch (e) {
      showToast("error", e.response?.data?.detail || "Erro ao carregar utilizadores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(q);
  }, [filter]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search (skip initial mount — handled by the effect above)
  useEffect(() => {
    if (q === "") return undefined;
    const t = setTimeout(() => {
      fetchUsers(q);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refreshDetail = async (email) => {
    try {
      const r = await api.get(`/admin/users/${encodeURIComponent(email)}`);
      setDetail(r.data);
    } catch (e) {
      showToast("error", "Erro ao carregar detalhes");
    }
  };

  const openUser = async (email) => {
    setSelected(email);
    setShowOrdersFor(null);
    setDetail(null);
    await refreshDetail(email);
  };

  const availableCasesToGrant = useMemo(() => {
    if (!detail || !allCases) return [];
    const unlocked = new Set(detail.unlocked_cases.map((c) => c.slug));
    return allCases.filter((c) => !unlocked.has(c.slug));
  }, [detail, allCases]);

  const grantAccess = async (caseSlug) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${encodeURIComponent(selected)}/grant-access`, { case_slug: caseSlug });
      showToast("success", "Acesso concedido");
      await refreshDetail(selected);
      await fetchUsers(q);
    } catch (e) {
      showToast("error", e.response?.data?.detail || "Erro ao conceder acesso");
    } finally { setBusy(false); }
  };

  const revokeAccess = async (caseSlug) => {
    if (!selected) return;
    if (!window.confirm(`Remover acesso manual a "${caseSlug}"?\n\n(Compras reais via Stripe NÃO são afetadas.)`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${encodeURIComponent(selected)}/revoke-access`, { case_slug: caseSlug });
      if (r.data.removed === 0) {
        showToast("error", "Nenhum acesso manual encontrado — esta compra é real e não pode ser removida.");
      } else {
        showToast("success", "Acesso manual removido");
      }
      await refreshDetail(selected);
      await fetchUsers(q);
    } catch (e) {
      showToast("error", e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  const sendRecovery = async (label) => {
    if (!selected) return;
    if (!window.confirm(`Enviar email de redefinição de password para ${selected}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${encodeURIComponent(selected)}/send-recovery`, {});
      showToast("success", `${label} enviado para ${selected}`);
    } catch (e) {
      showToast("error", e.response?.data?.detail || "Erro ao enviar email");
    } finally { setBusy(false); }
  };

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

  const createPlaceholder = async () => {
    const email = q.trim().toLowerCase();
    if (!isEmail(email)) {
      showToast("error", "Email inválido");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/users", { email });
      showToast("success", `Utilizador ${email} adicionado. Concede agora o acesso.`);
      setQ("");
      await fetchUsers();
      // Auto-open the new user's detail panel
      await openUser(email);
    } catch (e) {
      showToast("error", e.response?.data?.detail || "Erro ao adicionar utilizador");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="users-admin-root" className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6">
      {/* Toast */}
      {toast && (
        <div
          data-testid="users-toast"
          className={`fixed top-6 right-6 z-50 mono text-[11px] tracking-[0.2em] px-4 py-3 border ${
            toast.type === "success" ? "border-blood bg-blood/10 text-white" : "border-yellow-500 bg-yellow-500/10 text-yellow-300"
          }`}
        >
          {toast.type === "success" ? <CheckCircle2 className="inline w-3.5 h-3.5 mr-2" /> : <AlertCircle className="inline w-3.5 h-3.5 mr-2" />}
          {toast.message}
        </div>
      )}

      {/* LEFT — search + list */}
      <div className="bg-ink-100 border border-white/10 p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-bone-faded" />
            <input
              data-testid="users-search-input"
              type="text"
              placeholder="Procurar por email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full bg-ink-50 border border-white/10 pl-9 pr-3 py-2.5 mono text-xs text-white tracking-wide focus:outline-none focus:border-blood/50"
            />
          </div>
          <button
            data-testid="users-refresh-btn"
            onClick={() => fetchUsers(q)}
            className="p-2.5 border border-white/10 hover:border-white/30 transition-colors text-bone-muted hover:text-white"
            title="Recarregar"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-4 pb-3 border-b border-white/10">
          <Filter className="w-3 h-3 text-bone-faded" />
          {[
            { v: "all", label: "TODOS" },
            { v: "paid", label: "COM COMPRA" },
            { v: "no_purchase", label: "SEM COMPRA" },
          ].map((f) => (
            <button
              key={f.v}
              data-testid={`users-filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`mono text-[10px] tracking-[0.25em] px-3 py-1.5 border ${
                filter === f.v
                  ? "border-blood bg-blood text-white"
                  : "border-white/15 text-bone-muted hover:text-white hover:border-white/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="space-y-px bg-white/5 max-h-[70vh] overflow-y-auto">
          {loading && (
            <p className="bg-ink-50 p-4 text-bone-muted mono text-xs">A carregar...</p>
          )}
          {!loading && users.length === 0 && (
            <div className="bg-ink-50 p-4">
              <p data-testid="users-empty" className="text-bone-muted mono text-xs mb-3">
                SEM UTILIZADORES
              </p>
              {isEmail(q) && (
                <button
                  data-testid="users-add-new-btn"
                  onClick={createPlaceholder}
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 mono text-[10px] tracking-[0.25em] text-blood border border-blood/40 hover:bg-blood/10 px-3 py-2.5 transition-colors disabled:opacity-50"
                >
                  + ADICIONAR {q.trim().toLowerCase()}
                </button>
              )}
            </div>
          )}
          {users.map((u) => (
            <button
              key={u.email}
              data-testid={`user-row-${u.email}`}
              onClick={() => openUser(u.email)}
              className={`w-full text-left bg-ink-50 p-3 hover:bg-ink-50/60 transition-colors ${
                selected === u.email ? "border-l-2 border-blood" : "border-l-2 border-transparent"
              }`}
            >
              <div className="flex items-center gap-2 justify-between">
                <p className="text-sm text-white truncate flex-1">{u.email}</p>
                <StatusBadge status={u.status} />
              </div>
              <div className="flex items-center gap-3 mt-1 mono text-[10px] text-bone-faded tracking-[0.15em]">
                <span className="flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> {u.purchases_count}</span>
                <span className="flex items-center gap-1"><FolderLock className="w-3 h-3" /> {u.unlocked_cases.length}</span>
                {u.last_login && (
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(u.last_login).split(",")[0]}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT — detail panel */}
      <div className="bg-ink-100 border border-white/10 p-5 lg:p-6">
        {!selected && (
          <div data-testid="users-empty-detail" className="text-center py-20">
            <FolderLock className="w-10 h-10 text-bone-faded mx-auto mb-4" />
            <p className="mono text-[11px] tracking-[0.3em] text-bone-muted">
              SELECIONA UM UTILIZADOR
            </p>
          </div>
        )}

        {selected && !detail && (
          <p className="text-bone-muted mono text-xs">A carregar detalhes...</p>
        )}

        {selected && detail && (
          <div data-testid="user-detail-panel">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <p className="mono text-[10px] tracking-[0.3em] text-blood">UTILIZADOR</p>
                  <StatusBadge status={detail.status} />
                </div>
                <h2 data-testid="user-detail-email" className="headline text-2xl md:text-3xl break-all">{detail.email}</h2>
              </div>
              <button
                onClick={() => { setSelected(null); setDetail(null); }}
                className="p-2 border border-white/10 hover:border-white/30 transition-colors text-bone-muted hover:text-white"
                title="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mb-6">
              <Stat label="DATA 1ª COMPRA" value={formatDate(detail.first_purchase_at)} />
              <Stat label="Nº COMPRAS" value={detail.purchases_count} testid="stat-purchases" />
              <Stat label="CASOS DESBLOQUEADOS" value={detail.unlocked_cases.length} testid="stat-unlocked" />
              <Stat label="ÚLTIMO LOGIN" value={formatDate(detail.last_login)} />
            </div>

            {/* Unlocked cases */}
            <div className="mb-6">
              <p className="mono text-[10px] tracking-[0.3em] text-bone-faded mb-3">CASOS DESBLOQUEADOS</p>
              {detail.unlocked_cases.length === 0 ? (
                <p className="mono text-xs text-bone-muted">SEM CASOS DESBLOQUEADOS</p>
              ) : (
                <div className="space-y-px bg-white/10">
                  {detail.unlocked_cases.map((c) => (
                    <div key={c.slug} className="bg-ink-50 p-3 flex items-center justify-between gap-3" data-testid={`unlocked-case-${c.slug}`}>
                      <div className="min-w-0">
                        <p className="mono text-[10px] text-blood tracking-[0.25em]">{c.code || "—"}</p>
                        <p className="text-sm truncate">{c.title}</p>
                      </div>
                      <button
                        data-testid={`revoke-${c.slug}`}
                        onClick={() => revokeAccess(c.slug)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.25em] text-bone-muted hover:text-blood transition-colors border border-white/10 hover:border-blood px-3 py-1.5 disabled:opacity-50"
                      >
                        <Minus className="w-3 h-3" /> REMOVER
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Grant access */}
            <div className="mb-6">
              <p className="mono text-[10px] tracking-[0.3em] text-bone-faded mb-3">DAR ACESSO MANUAL A UM CASO</p>
              {availableCasesToGrant.length === 0 ? (
                <p className="mono text-xs text-bone-muted">Todos os casos já estão desbloqueados.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {availableCasesToGrant.map((c) => (
                    <button
                      key={c.slug}
                      data-testid={`grant-${c.slug}`}
                      onClick={() => grantAccess(c.slug)}
                      disabled={busy}
                      className="inline-flex items-center gap-2 mono text-[10px] tracking-[0.25em] text-white border border-white/15 hover:border-blood hover:text-blood px-3 py-2 transition-colors disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" /> {c.code || c.slug} · {c.title}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6">
              <button
                data-testid="action-resend-access"
                onClick={() => sendRecovery("Email de acesso")}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 mono text-[10px] tracking-[0.25em] text-white border border-white/15 hover:border-white hover:bg-white/5 px-3 py-3 transition-colors disabled:opacity-50"
              >
                <Mail className="w-3.5 h-3.5" /> REENVIAR EMAIL DE ACESSO
              </button>
              <button
                data-testid="action-force-reset"
                onClick={() => sendRecovery("Email de redefinição")}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 mono text-[10px] tracking-[0.25em] text-white border border-white/15 hover:border-white hover:bg-white/5 px-3 py-3 transition-colors disabled:opacity-50"
              >
                <KeyRound className="w-3.5 h-3.5" /> FORÇAR REDEFINIÇÃO DE PASSWORD
              </button>
              <button
                data-testid="action-view-orders"
                onClick={() => setShowOrdersFor(showOrdersFor === selected ? null : selected)}
                className="inline-flex items-center justify-center gap-2 mono text-[10px] tracking-[0.25em] text-blood border border-blood/30 hover:bg-blood/10 px-3 py-3 transition-colors"
              >
                <ShoppingBag className="w-3.5 h-3.5" /> {showOrdersFor === selected ? "OCULTAR COMPRAS" : "VER COMPRAS"}
              </button>
            </div>

            {/* Purchase history */}
            {showOrdersFor === selected && (
              <div data-testid="orders-history" className="border-t border-white/10 pt-5">
                <p className="mono text-[10px] tracking-[0.3em] text-bone-faded mb-3">
                  HISTÓRICO DE COMPRAS ({detail.orders.length})
                </p>
                {detail.orders.length === 0 ? (
                  <p className="mono text-xs text-bone-muted">SEM COMPRAS</p>
                ) : (
                  <div className="space-y-px bg-white/10 max-h-96 overflow-y-auto">
                    {detail.orders.map((o) => (
                      <div key={o.session_id} className="bg-ink-50 p-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                        <div className="md:col-span-2">
                          <p className="mono text-[9px] text-bone-faded tracking-[0.2em]">SESSÃO</p>
                          <p className="mono text-[11px] truncate">{o.session_id?.slice(0, 28)}</p>
                          {o.source === "manual_grant" && (
                            <span className="mono text-[9px] text-blood tracking-[0.25em]">MANUAL</span>
                          )}
                        </div>
                        <div>
                          <p className="mono text-[9px] text-bone-faded tracking-[0.2em]">CASO</p>
                          <p className="truncate">{o.case_title || o.case_slug}</p>
                        </div>
                        <div>
                          <p className="mono text-[9px] text-bone-faded tracking-[0.2em]">VALOR</p>
                          <p>{(o.amount ?? 0).toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="mono text-[9px] text-bone-faded tracking-[0.2em]">STATUS · DATA</p>
                          <p className={`mono text-[10px] uppercase tracking-[0.2em] ${o.payment_status === "paid" ? "text-blood" : "text-bone-muted"}`}>
                            {o.payment_status}
                          </p>
                          <p className="text-[10px] text-bone-muted">{formatDate(o.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-ink-50 p-3" data-testid={testid}>
      <p className="mono text-[9px] tracking-[0.3em] text-bone-faded">{label}</p>
      <p className="text-sm text-white mt-1 truncate">{value}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    comprou:   { text: "COMPROU",   cls: "border-blood text-blood" },
    registado: { text: "REGISTADO", cls: "border-white/30 text-bone-muted" },
    manual:    { text: "MANUAL",    cls: "border-yellow-500/50 text-yellow-400" },
  };
  const m = map[status] || map.registado;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`mono text-[8px] tracking-[0.3em] px-1.5 py-0.5 border ${m.cls} shrink-0`}
    >
      {m.text}
    </span>
  );
}
